import { useCallback, useEffect, useMemo, useState } from 'react';

import { faFileImage, faFileLines, faRotateBack, faRotateForward } from '@fortawesome/pro-solid-svg-icons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Grid } from '@progress/kendo-react-grid';
import { homeIcon } from '@progress/kendo-svg-icons';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { useEvent, useWindowSize } from 'core/hooks';
import { NotificationsService } from 'core/notifications';
import { Breadcrumb, Button, Card, CollapsibleSidebar, FlatIconButton, Icon, TabButton, TabContainer, TabsContainer, useBreakpoints } from 'core/ui';
import { equalsInsensitive } from 'core/utils';

import { useApiClient } from 'features/api';
import { ApiWebSocketProvider } from 'features/api/fragments/ApiWebSocketProvider';
import { useMutationFiles } from 'features/api/hooks/useMutationFiles';
import { useAccessTokenSnapshot } from 'features/auth/hooks/useAccessTokenSnapshot';
import { ExamActions } from 'features/exam/redux/actions';
import { ExamSelectors } from 'features/exam/redux/selectors';
import { FileSelectSidebar, FileViewer } from 'features/file';
import { FileViewerIntegrated } from 'features/patient/fragments/FileViewerIntegrated';
import { PatientFiles } from 'features/patient/fragments/PatientFiles';
import { PatientGridService } from 'features/patient/services';
import { SettingsHelpers, useUserSettings } from 'features/settings';
import { WellKnownSettingName } from 'features/settings/types/WellKnownSettingName';
import { useTagLookups } from 'features/tags/hooks/useTagLookups';

import { ExamStatus } from '../../exam/constants';
import { useExamFiles, useNextExamId } from '../../exam/hooks';
import { ExamReadSubmitType } from '../constants';
import { ExamReadingActions } from '../redux';
import { ExamReadsDataService } from '../services';
import { ExamReadSidebarBody } from './ExamReadSidebarBody';
import { UndoAddReadMessage } from './UndoAddReadMessage';

const MOBILE_WIDTH_BREAKPOINT = 768;

export const ExamRead = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { homePageUrl, fullScreenReading } = useUserSettings();
  const { rotateFileMutation } = useMutationFiles(id);

  const { userSettings } = useUserSettings(false);
  const apiClient = useApiClient();

  // State for integrated file viewer
  const [integratedViewerFile, setIntegratedViewerFile] = useState(null);
  const [integratedViewerContext, setIntegratedViewerContext] = useState([]);

  // Fetch tag data for the integrated viewer
  const { allFlatTags: fetchedFlatTags, allHierarchicalTags: fetchedHierarchicalTags, tagIdToTagMap: fetchedTagIdToTagMap, isLoadingTags } = useTagLookups();
  const [allTagsFlatState, setAllTagsFlatState] = useState([]);
  const [allHierarchicalTagsState, setAllHierarchicalTagsState] = useState([]);
  const [tagIdToTagMapState, setTagIdToTagMapState] = useState(new Map());

  useEffect(() => {
    if (!isLoadingTags) {
      setAllTagsFlatState(fetchedFlatTags ?? []);
      setAllHierarchicalTagsState(fetchedHierarchicalTags ?? []);
      setTagIdToTagMapState(fetchedTagIdToTagMap ?? new Map());
    }
  }, [fetchedFlatTags, fetchedHierarchicalTags, fetchedTagIdToTagMap, isLoadingTags]);

  // Destructure the state to get value and setter
  const [adminTags, setAdminTags] = useState(null);

  // Fetch admin tags on component mount
  useEffect(() => {
    const fetchAdminTags = async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        const tags = await apiClient.tagsClient.getAllAdminTags();
        setAdminTags(tags);
      } catch (error) {
        console.error('Failed to load admin tags:', error);
      }
    };

    fetchAdminTags();
  }, [apiClient.tagsClient]);

  const exam = useSelector(ExamSelectors.getById(id));
  const previousExamId = useSelector(ExamSelectors.getPreviousExamId);

  const [gridData, setGridData] = useState([]);
  const memoizedGridData = useMemo(() => gridData, [gridData]);

  const examFilter = searchParams.get('quickFilter') || ExamStatus.ALL.name;
  const groupId = searchParams.get('groupId');

  // get the current exam data state
  const worklistView = localStorage.getItem('currentWorklistViewODataStr');

  // get the next exam id
  const nextExamId = useNextExamId(id, examFilter, groupId, worklistView);
  const { width } = useWindowSize();

  // Check if the showFilesTab setting is enabled
  const showFilesTab = SettingsHelpers.findSettingByName(userSettings ?? [], WellKnownSettingName.ShowFilesTab, false)?.value === 'true';

  const [sidebarExpanded, setSidebarExpanded] = useState(showFilesTab ? false : width < MOBILE_WIDTH_BREAKPOINT ? false : true);

  const [popupWindows] = useState(null);

  const { accessToken } = useAccessTokenSnapshot();

  const { setVerticalClampOverride } = useBreakpoints();

  const [activeTab, setActiveTab] = useState(showFilesTab ? 'files' : 'fileViewer');

  const exams = useSelector(ExamSelectors.getAllGrid);

  useEffect(() => {
    console.log('useEffect setVerticalClampOverride');
    setVerticalClampOverride(true);

    return () => {
      setVerticalClampOverride(false);
    };
  }, [setVerticalClampOverride]);

  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { files, currentFile, setCurrentFile } = useExamFiles(id, exam?.statusType?.name, accessToken);

  // Set gridData for the files tab when exam data is available
  useEffect(() => {
    if (exam && showFilesTab && exam.patient_Id) {
      console.log('fetch grid exams', exams);

      // Get patient exams associated with the same patient

      const isOpo = exam?.patient?.locationType === 'OPO';
      const newGridData = PatientGridService.initializePatientGrid(exams, isOpo, exam?.patient);
      setGridData(newGridData);
      console.log('gridData', newGridData);
    }
  }, [exam, showFilesTab, dispatch, exams]);

  const breadcrumbs = useMemo(
    () => [
      // {
      //   id: 'home',
      //   text: 'Home',
      //   icon: <StyledIcon icon={homeIcon} />,
      //   navigateToExternal: homePageUrl,
      // },
    ],
    [homePageUrl],
  );

  const handleBreadcrumbClick = useCallback(
    (event) => {
      const selectedItem = breadcrumbs.find((item) => item.id === event.id);

      if (selectedItem.navigateTo) {
        navigate(selectedItem.navigateTo);

        return;
      }

      window.location = selectedItem.navigateToExternal;
    },
    [navigate, breadcrumbs],
  );

  const closeWindows = useCallback(() => {
    if (popupWindows) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      popupWindows.forEach((window) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        window.close();
      });
    }
  }, [popupWindows]);

  const removeUndoParam = useCallback((currentSearchParams) => {
    const newSearchParams = new URLSearchParams(currentSearchParams);
    newSearchParams.delete('undo');
    return newSearchParams.toString();
  }, []);

  const handleNextExamClick = useCallback(() => {
    closeWindows();
    dispatch(ExamActions.pushRecentlyViewedExam(id));
    const updatedSearchParams = removeUndoParam(searchParams);
    navigate(`/exam/${nextExamId}/read?${updatedSearchParams}`);
  }, [closeWindows, dispatch, id, navigate, nextExamId, searchParams, removeUndoParam]);

  const handlePrevExamClick = () => {
    closeWindows();
    dispatch(ExamActions.popRecentlyViewedExam());
    const updatedSearchParams = removeUndoParam(searchParams);
    navigate(`/exam/${previousExamId}/read?${updatedSearchParams}`);
  };

  const handleUndoClick = useCallback(
    (undoExamId) => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('undo', 'true');
      navigate(`/exam/${undoExamId}/read?${newSearchParams.toString()}`);
    },
    [navigate, searchParams],
  );

  const handleAddRead = useCallback(
    ({ values, event }) => {
      // TODO: show preview for For non alivecor, non-corrections radiology?

      // Ensure event and event.nativeEvent exist before accessing submitter
      if (!event?.nativeEvent?.submitter) {
        console.error('Event or submitter is undefined');
        return;
      }

      const { examData, readData } = ExamReadsDataService.getSubmitData({
        id,
        ...values,
      });

      const submitType = event.nativeEvent.submitter.id;

      if (submitType === ExamReadSubmitType.SAVE || submitType === ExamReadSubmitType.FINALIZE) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        dispatch(ExamReadingActions.saveRead({ ...examData, dynamicForms: readData })).then(() => {
          const updatedSearchParams = new URLSearchParams(searchParams);
          updatedSearchParams.delete('undo');

          if (submitType === ExamReadSubmitType.SAVE) {
            NotificationsService.displaySuccess('Saved.');
            setSearchParams(updatedSearchParams, { replace: true }); // Update URL in place
          } else if (submitType === ExamReadSubmitType.FINALIZE) {
            navigate(`/exam/${id}/read/approve?${updatedSearchParams.toString()}`);
          }
        });
      } else if (submitType === ExamReadSubmitType.APPROVE || submitType === ExamReadSubmitType.APPROVE_AND_NEXT) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        dispatch(
          ExamReadingActions.addRead({
            ...examData,
            dynamicForms: readData,
            isUndo: searchParams.get('undo') === 'true',
          }),
        ).then(() => {
          if (submitType === ExamReadSubmitType.APPROVE) {
            window.location = homePageUrl;
          } else {
            NotificationsService.removeAll(); // Dismiss any existing notifications
            NotificationsService.displaySuccess(<UndoAddReadMessage onUndo={() => handleUndoClick(id)} />, {
              autoClose: 5 * 1000, // 10s - reduced from 60s
              closeOnClick: false,
              hideProgressBar: false,
              pauseOnFocusLoss: false,
              pauseOnHover: false,
            });
            handleNextExamClick();
          }
        });
      } else {
        throw new Error('TODO: Not implemented.');
      }
    },
    [dispatch, handleNextExamClick, handleUndoClick, id, navigate, homePageUrl, searchParams, setSearchParams],
  );

  useEffect(() => {
    dispatch(ExamActions.getByIdAndLock(id));
  }, [dispatch, id]);

  // Handle tab switching
  const handleFileViewerTabClick = useCallback(() => {
    setActiveTab('fileViewer');
  }, []);

  const handleFilesTabClick = useCallback(() => {
    setActiveTab('files');
  }, []);

  // --- Integrated Viewer Handlers ---
  const handleViewFile = useCallback((file, context) => {
    setIntegratedViewerFile(file);
    setIntegratedViewerContext(context);
    setActiveTab('files'); // Ensure the 'files' tab content area is active to host the viewer
  }, []);

  const handleCloseViewerIntegrated = useCallback(() => {
    setIntegratedViewerFile(null);
    setIntegratedViewerContext([]);
  }, []);

  const handleDeleteFile = useCallback(
    async (fileId) => {
      const numericFileId = typeof fileId === 'string' ? parseInt(fileId, 10) : fileId;
      if (isNaN(numericFileId)) return;
      // Consider adding a confirmation dialog if not present in FileViewerIntegrated
      // if (!window.confirm(`Are you sure you want to delete this file?`)) return;
      try {
        await apiClient.filesClient.deleteFile(numericFileId.toString(), 'msal-required');
        NotificationsService.displaySuccess('File deleted successfully.');
        handleCloseViewerIntegrated(); // Close viewer or update context if file list is maintained here
        // Potentially re-fetch files for the PatientFiles component if it doesn't auto-update
      } catch (error) {
        NotificationsService.displayError('Failed to delete file.');
        console.error('Delete file error:', error);
      }
    },
    [apiClient.filesClient, handleCloseViewerIntegrated],
  );

  const handleRenameFile = useCallback(
    async (file, newName) => {
      if (!newName || newName === file.fileName) return;
      try {
        const patchDoc = [{ op: 'replace', path: '/fileName', value: newName }];
        await apiClient.filesClient.renameFile(file.id, patchDoc);
        NotificationsService.displaySuccess('File renamed successfully.');
        setIntegratedViewerFile((prev) => (prev ? { ...prev, fileName: newName } : null));
        setIntegratedViewerContext((prev) => prev.map((f) => (f.id === file.id ? { ...f, fileName: newName } : f)));
        // Potentially re-fetch files for the PatientFiles component
      } catch (error) {
        NotificationsService.displayError('Failed to rename file.');
        console.error('Rename file error:', error);
        throw error; // Re-throw for FileViewerIntegrated to handle
      }
    },
    [apiClient.filesClient],
  );

  const handleUpdateFileTimestamps = useCallback(
    async (file, captureTime, eventTime) => {
      const formatForApi = (date) => (date ? dayjs(date).toISOString() : null);
      const updatePayload = {};
      const stateUpdatePayload = {};

      if (captureTime !== undefined) {
        const formattedTime = formatForApi(captureTime);
        updatePayload.captureTime = formattedTime;
        stateUpdatePayload.captureTime = formattedTime;
      }
      if (eventTime !== undefined) {
        const formattedTime = formatForApi(eventTime);
        updatePayload.eventTime = formattedTime;
        stateUpdatePayload.eventTime = formattedTime;
      }

      if (Object.keys(updatePayload).length === 0) return;

      try {
        const patchDoc = [];
        if ('captureTime' in updatePayload) {
          patchDoc.push({ op: 'replace', path: '/captureTime', value: updatePayload.captureTime ?? null });
        }
        if ('eventTime' in updatePayload) {
          patchDoc.push({ op: 'replace', path: '/eventTime', value: updatePayload.eventTime ?? null });
        }

        if (patchDoc.length === 0) return;

        await apiClient.filesClient.renameFile(file.id, patchDoc); // renameFile is used for patching
        NotificationsService.displaySuccess('Timestamps updated successfully.');
        setIntegratedViewerFile((prev) => (prev ? { ...prev, ...stateUpdatePayload } : null));
        setIntegratedViewerContext((prev) => prev.map((f) => (f.id === file.id ? { ...f, ...stateUpdatePayload } : f)));
        // Potentially re-fetch files for the PatientFiles component
      } catch (error) {
        NotificationsService.displayError('Failed to update timestamps.');
        console.error('Timestamp update error:', error);
        throw error; // Re-throw for FileViewerIntegrated to handle
      }
    },
    [apiClient.filesClient],
  );

  const handleDownloadFile = useCallback(
    async (file) => {
      if (!file?.location) {
        NotificationsService.displayError('File location is missing, cannot generate download link.');
        return;
      }
      try {
        const locationParts = file.location.split('/');
        if (locationParts.length < 2) {
          NotificationsService.displayError('Invalid file location format.');
          console.error('Invalid file location format:', file.location);
          return;
        }
        const containerName = locationParts[0];
        const blobName = locationParts.slice(1).join('/');

        const downloadSasUrl = await apiClient.filesClient.generateAzureBlobDownloadSasUrl(containerName, blobName);

        if (downloadSasUrl) {
          const link = document.createElement('a');
          link.href = downloadSasUrl;
          link.setAttribute('download', file.fileName || `download_${file.id}`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          NotificationsService.displayError('Could not get download URL.');
        }
      } catch (error) {
        NotificationsService.displayError('Failed to download file.');
        console.error('Download file error:', error);
      }
    },
    [apiClient.filesClient],
  );

  const handleUpdateFileTags = useCallback(
    async (fileId, tagIds) => {
      try {
        await apiClient.filesClient.updateFileTags(fileId, tagIds, 'msal-required');
        NotificationsService.displaySuccess('Tags updated successfully.');
        const updatedTags = tagIds.map((tagId) => tagIdToTagMapState.get(tagId)).filter(Boolean);
        setIntegratedViewerFile((prev) => (prev && prev.id === fileId ? { ...prev, tags: updatedTags } : prev));
        setIntegratedViewerContext((prev) => prev.map((f) => (f.id === fileId ? { ...f, tags: updatedTags } : f)));
        // Potentially re-fetch files for the PatientFiles component
      } catch (error) {
        NotificationsService.displayError('Failed to update tags.');
        console.error('Update tags error:', error);
        throw error; // Re-throw for FileViewerIntegrated to handle
      }
    },
    [apiClient.filesClient, tagIdToTagMapState],
  );

  const handleRotateBackClick = useEvent(async () => {
    const result = await rotateFileMutation.mutateAsync({ file: currentFile, degrees: -90 });
    setCurrentFile(result);
  });

  const handleRotateForwardClick = useEvent(async () => {
    const result = await rotateFileMutation.mutateAsync({ file: currentFile, degrees: 90 });
    setCurrentFile(result);
  });

  return (
    <StyledReadPage>
      <CollapsibleSidebar
        header={<Breadcrumb data={breadcrumbs} onItemSelect={handleBreadcrumbClick} onKeyDown={handleBreadcrumbClick} />}
        body={
          <ExamReadSidebarBody
            exam={exam}
            handleNextExamClick={nextExamId ? handleNextExamClick : undefined}
            handlePrevExamClick={previousExamId ? handlePrevExamClick : undefined}
            handleSubmit={handleAddRead}
            adminTags={adminTags}
            isUndo={searchParams.get('undo') === 'true'}
          />
        }
        expanded={sidebarExpanded}
        onExpandChange={setSidebarExpanded}
        width={
          fullScreenReading
            ? window.innerWidth - 120 // full screen by user setting
            : 400
        }
      >
        <StyledMainContainer showFilesTab={showFilesTab}>
          {showFilesTab &&
            !integratedViewerFile && ( // Hide tabs when integrated viewer is open
              <StyledTabsContainer>
                <TabButton type="button" selected={activeTab === 'fileViewer'} roundedCorners="left" onClick={handleFileViewerTabClick}>
                  <Icon icon={faFileImage} style={{ marginRight: '8px' }} />
                  Viewer
                </TabButton>
                <TabButton type="button" selected={activeTab === 'files'} roundedCorners="right" onClick={handleFilesTabClick}>
                  <Icon icon={faFileLines} style={{ marginRight: '8px' }} />
                  Files
                </TabButton>
              </StyledTabsContainer>
            )}

          {integratedViewerFile ? (
            <FileViewerIntegrated
              viewingFile={integratedViewerFile}
              viewingFileContext={integratedViewerContext}
              onCloseViewer={handleCloseViewerIntegrated}
              onFileDeleted={handleDeleteFile}
              onFileRenamed={handleRenameFile}
              onFileTimestampsUpdated={handleUpdateFileTimestamps}
              onFileTagsUpdated={handleUpdateFileTags}
              onFileDownloaded={handleDownloadFile}
              allTagsFlat={allTagsFlatState}
              allHierarchicalTags={allHierarchicalTagsState}
              tagIdToTagMap={tagIdToTagMapState}
              areTagsLoading={isLoadingTags}
              patientId={Number(exam?.patient_Id || 0)}
              currentPath=""
              selectedWorklist={null}
              allWorklists={null}
              stackName={undefined}
            />
          ) : activeTab === 'fileViewer' && currentFile ? (
            <StyledFileViewerContainerDiv>
              <StyledRotateToolbarDiv>
                {equalsInsensitive(currentFile.fileType, 'PDF') && (
                  <>
                    <FlatIconButton onClick={handleRotateBackClick}>
                      <Icon icon={faRotateBack} />
                    </FlatIconButton>
                    <FlatIconButton onClick={handleRotateForwardClick}>
                      <Icon icon={faRotateForward} />
                    </FlatIconButton>
                  </>
                )}
              </StyledRotateToolbarDiv>

              <StyledFileViewer file={currentFile} />
            </StyledFileViewerContainerDiv>
          ) : activeTab === 'files' && exam?.patient_Id ? (
            <ApiWebSocketProvider>
              <PatientFiles patientId={Number(exam.patient_Id)} gridData={memoizedGridData} hideRightSidebar={true} onViewFile={handleViewFile} />
            </ApiWebSocketProvider>
          ) : null}
        </StyledMainContainer>
        {files?.length > 1 &&
          activeTab === 'fileViewer' &&
          !integratedViewerFile && ( // Hide file select when integrated viewer is open
            <FileSelectSidebar
              defaultCollapsed={width < MOBILE_WIDTH_BREAKPOINT}
              files={files}
              handleFileSelect={(file) => setCurrentFile(file)}
              selectedFile={currentFile}
              accessToken={accessToken}
              examId={id}
            />
          )}
      </CollapsibleSidebar>
    </StyledReadPage>
  );
};

ExamRead.displayName = 'ExamRead';

const StyledFileViewerContainerDiv = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: min-content minmax(0, 1fr);
`;

const StyledRotateToolbarDiv = styled.div`
  display: flex;
`;

const StyledFileViewer = styled(FileViewer)`
  flex: 1 1 auto;
  min-height: auto;
  max-width: 100%;
`;

const StyledReadPage = styled.div`
  height: 100vh;
  width: 100vw;
`;

const StyledMainContainer = styled.div`
  display: grid;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  overflow: auto;
  ${({ showFilesTab }) => (showFilesTab ? `grid-template-rows: min-content minmax(0, 1fr);` : `grid-template-rows: minmax(0, 1fr);`)}

  .file-viewer-page {
    height: 100%;
    overflow: hidden !important;
    width: 100% !important;
    max-height: 100vh !important; //todo: improve auto calculation
    min-height: calc(100vh - 130px);
  }
`;

const StyledDivContent = styled.div`
  flex: 1 1 0;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  overflow: auto;
  contain: strict;
  margin-top: ${({ showFilesTab, integratedViewerFile }) => (integratedViewerFile ? '40px' : showFilesTab ? '40px' : '0')};
  background-color: #fff;
`;

const StyledIcon = styled(SvgIcon)`
  color: ${({ theme }) => theme.colors.primary};
`;

const StyledTabsContainer = styled.div`
  display: flex;
  //padding: ${({ theme }) => theme.space.spacing10} 0;
  //justify-content: left !important;
  //position: absolute;
  //margin-left: 2em;
  background-color: #fff;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 800;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
`;
